import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api, { resolveUploadUrl } from '../../config/api';
import { formatPhoneDisplay } from '../../utils/formatPhoneDisplay';
import { formatBirthDateDisplayUtc } from '../../utils/dateInput';
import { FaCheck, FaTimes, FaEye, FaSearch, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './AdminPages.css';
import './AdminSubscribers.css';

const ITEMS_PER_PAGE = 50;

const AdminNewMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt-desc');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchNewMembers();
  }, []);

  const fetchNewMembers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/new-members');
      setMembers(response.data);
    } catch (error) {
      console.error('Error fetching new members:', error);
      alert('Erreur lors du chargement des nouveaux membres');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (memberId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir valider l\'abonnement de ce membre ?')) {
      return;
    }

    try {
      await api.put(`/users/${memberId}/validate-membership`);
      alert('Abonnement validé avec succès');
      fetchNewMembers();
      // Remove from selected if it was selected
      setSelectedMembers(prev => prev.filter(id => id !== memberId));
    } catch (error) {
      console.error('Error validating membership:', error);
      alert(error.response?.data?.error || 'Erreur lors de la validation');
    }
  };

  const handleReject = async (memberId) => {
    const reason = window.prompt('Raison du refus (optionnel):');
    if (reason === null) return; // User cancelled

    if (!window.confirm('Êtes-vous sûr de vouloir refuser l\'abonnement de ce membre ?')) {
      return;
    }

    try {
      await api.put(`/users/${memberId}/reject-membership`, { reason });
      alert('Abonnement refusé');
      fetchNewMembers();
      // Remove from selected if it was selected
      setSelectedMembers(prev => prev.filter(id => id !== memberId));
    } catch (error) {
      console.error('Error rejecting membership:', error);
      alert(error.response?.data?.error || 'Erreur lors du refus');
    }
  };

  // Filter by search, then sort (default: latest registered first)
  const filteredMembers = useMemo(() => {
    let list = members;
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      list = members.filter((member) => {
        const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
        const email = member.email?.toLowerCase() || '';
        return fullName.includes(search) || email.includes(search);
      });
    }

    const sorted = [...list];
    const date = (m) => (m.createdAt ? new Date(m.createdAt).getTime() : 0);

    switch (sortBy) {
      case 'createdAt-desc':
        sorted.sort((a, b) => date(b) - date(a));
        break;
      case 'createdAt-asc':
        sorted.sort((a, b) => date(a) - date(b));
        break;
      case 'name-asc':
        sorted.sort((a, b) =>
          `${a.lastName || ''} ${a.firstName || ''}`.localeCompare(
            `${b.lastName || ''} ${b.firstName || ''}`,
            'fr',
            { sensitivity: 'base' }
          )
        );
        break;
      case 'name-desc':
        sorted.sort((a, b) =>
          `${b.lastName || ''} ${b.firstName || ''}`.localeCompare(
            `${a.lastName || ''} ${a.firstName || ''}`,
            'fr',
            { sensitivity: 'base' }
          )
        );
        break;
      case 'email-asc':
        sorted.sort((a, b) => (a.email || '').localeCompare(b.email || '', 'fr', { sensitivity: 'base' }));
        break;
      default:
        sorted.sort((a, b) => date(b) - date(a));
    }

    return sorted;
  }, [members, searchTerm, sortBy]);

  // Reset to page 1 when search or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredMembers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedMembers = filteredMembers.slice(startIndex, endIndex);

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Add all paginated members to selection
      const paginatedIds = paginatedMembers.map(m => m._id);
      setSelectedMembers(prev => {
        const combined = [...new Set([...prev, ...paginatedIds])];
        return combined;
      });
    } else {
      // Remove all paginated members from selection
      const paginatedIds = paginatedMembers.map(m => m._id);
      setSelectedMembers(prev => prev.filter(id => !paginatedIds.includes(id)));
    }
  };

  // Check if all paginated members are selected
  const allPaginatedSelected = paginatedMembers.length > 0 && 
    paginatedMembers.every(m => selectedMembers.includes(m._id));

  const handleSelectMember = (memberId) => {
    setSelectedMembers(prev => 
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleValidateMultiple = async () => {
    if (selectedMembers.length === 0) {
      alert('Veuillez sélectionner au moins un membre à valider');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir valider l'abonnement de ${selectedMembers.length} membre(s) ?`)) {
      return;
    }

    try {
      // Validate all selected members
      const promises = selectedMembers.map(memberId => 
        api.put(`/users/${memberId}/validate-membership`)
      );
      
      await Promise.all(promises);
      alert(`${selectedMembers.length} abonnement(s) validé(s) avec succès`);
      fetchNewMembers();
      setSelectedMembers([]);
    } catch (error) {
      console.error('Error validating memberships:', error);
      alert(error.response?.data?.error || 'Erreur lors de la validation en masse');
    }
  };

  const handleViewDetails = (member) => {
    setSelectedMember(member);
    setShowDetailModal(true);
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    let filename;
    if (path.includes('/')) {
      const pathParts = path.split('/');
      filename = pathParts[pathParts.length - 1];
    } else {
      filename = path;
    }
    return resolveUploadUrl(`/uploads/${filename}`);
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateOnly = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div
        className="admin-header"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '1rem',
        }}
      >
        <div>
          <h1>Nouveaux Membres Inscrits</h1>
          <p>Valider ou refuser les abonnements des nouveaux membres</p>
        </div>
        <Link to="/admin/register-member" className="btn-add" style={{ textDecoration: 'none', flexShrink: 0 }}>
          Inscrire un membre
        </Link>
      </div>

      {/* Search/Filter Section */}
      {members.length > 0 && (
        <div className="filters-section">
          <div className="search-box">
            <FaSearch />
            <input
              type="text"
              placeholder="Rechercher par nom ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="clear-search"
                onClick={() => setSearchTerm('')}
                title="Effacer la recherche"
              >
                <FaTimes />
              </button>
            )}
          </div>

          <div className="sort-controls">
            <div className="filter-group">
              <label htmlFor="sort-new-members">Trier par :</label>
              <select
                id="sort-new-members"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="createdAt-desc">Inscription (plus récent d&apos;abord)</option>
                <option value="createdAt-asc">Inscription (plus ancien d&apos;abord)</option>
                <option value="name-asc">Nom (A → Z)</option>
                <option value="name-desc">Nom (Z → A)</option>
                <option value="email-asc">Email (A → Z)</option>
              </select>
            </div>
          </div>
          
          {/* Results count */}
          <div className="results-count">
            {filteredMembers.length > 0 ? (
              <p>
                Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(endIndex, filteredMembers.length)}</strong> sur <strong>{filteredMembers.length}</strong> membre(s)
              </p>
            ) : (
              <p>Aucun résultat</p>
            )}
          </div>
          
          {/* Selection bar */}
          {selectedMembers.length > 0 && (
            <div className="selection-bar">
              <span>
                <strong>{selectedMembers.length}</strong> membre(s) sélectionné(s)
              </span>
              <div className="selection-actions">
                <button
                  className="btn-select-action"
                  onClick={handleValidateMultiple}
                >
                  <FaCheck /> Valider les sélectionnés
                </button>
                <button
                  className="btn-select-action btn-deselect"
                  onClick={() => setSelectedMembers([])}
                >
                  Tout désélectionner
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {members.length === 0 ? (
        <div className="empty-state">
          <p>Aucun nouveau membre en attente de validation</p>
        </div>
      ) : (
        <div className="members-list">
          {filteredMembers.length === 0 ? (
            <div className="empty-state">
              <p>Aucun membre ne correspond à votre recherche</p>
            </div>
          ) : (
            <>
              {/* Select all checkbox */}
              {paginatedMembers.length > 0 && (
                <div className="select-all-container">
                  <label className="select-all-checkbox">
                    <input
                      type="checkbox"
                      checked={allPaginatedSelected}
                      onChange={handleSelectAll}
                    />
                    <span>Sélectionner tout sur cette page ({paginatedMembers.length} membre(s))</span>
                  </label>
                </div>
              )}
              
              {paginatedMembers.map((member) => (
                <div 
                  key={member._id} 
                  className={`member-card pending ${selectedMembers.includes(member._id) ? 'selected' : ''}`}
                >
                  <div className="member-card-header">
                    <div className="member-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(member._id)}
                        onChange={() => handleSelectMember(member._id)}
                      />
                    </div>
                    <div className="member-info">
                  <h3>
                    {member.civilite === 'M' ? 'M.' : 'Mme'} {member.firstName} {member.lastName}
                  </h3>
                  <p className="member-email">{member.email}</p>
                  <p className="member-date">
                    Inscrit le: {formatDate(member.createdAt)}
                  </p>
                </div>
                <div className="member-actions">
                  <button
                    className="btn-view-details"
                    onClick={() => handleViewDetails(member)}
                  >
                    <FaEye /> Détails
                  </button>
                  <button
                    className="btn-validate"
                    onClick={() => handleValidate(member._id)}
                  >
                    <FaCheck /> Valider
                  </button>
                  <button
                    className="btn-reject"
                    onClick={() => handleReject(member._id)}
                  >
                    <FaTimes /> Refuser
                  </button>
                </div>
              </div>
            </div>
              ))}
              
              {/* Detail Modal */}
              {showDetailModal && selectedMember && (
                <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
                  <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                      <h2>
                        Détails - {selectedMember.civilite === 'M' ? 'M.' : 'Mme'} {selectedMember.firstName} {selectedMember.lastName}
                      </h2>
                      <button className="btn-close" onClick={() => setShowDetailModal(false)}>×</button>
                    </div>
                    
                    <div className="subscriber-details">
                      <div className="detail-section">
                        <h3>Informations personnelles</h3>
                        <div className="detail-grid">
                          <div className="detail-item">
                            <label>Civilité:</label>
                            <span>{selectedMember.civilite === 'M' ? 'Monsieur' : 'Madame'}</span>
                          </div>
                          <div className="detail-item">
                            <label>Nom:</label>
                            <span>{selectedMember.lastName}</span>
                          </div>
                          <div className="detail-item">
                            <label>Prénom:</label>
                            <span>{selectedMember.firstName}</span>
                          </div>
                          <div className="detail-item">
                            <label>Sexe:</label>
                            <span>{selectedMember.sexe}</span>
                          </div>
                          <div className="detail-item">
                            <label>Date de naissance:</label>
                            <span>
                              {selectedMember.dateOfBirth
                                ? formatBirthDateDisplayUtc(selectedMember.dateOfBirth, 'fr-CH') || '—'
                                : '—'}
                            </span>
                          </div>
                          <div className="detail-item">
                            <label>Téléphone:</label>
                            <span>{formatPhoneDisplay(selectedMember.phone)}</span>
                          </div>
                          <div className="detail-item">
                            <label>Email:</label>
                            <span>{selectedMember.email}</span>
                          </div>
                          <div className="detail-item">
                            <label>Email secondaire:</label>
                            <span>{selectedMember.secondaryEmail || '—'}</span>
                          </div>
                          <div className="detail-item">
                            <label>Langue préférée:</label>
                            <span>{selectedMember.preferredLanguage === 'fr' ? 'Français' : 'Italien'}</span>
                          </div>
                        </div>
                      </div>

                      {selectedMember.birthPlace && (
                        <div className="detail-section">
                          <h3>Lieu de naissance</h3>
                          <div className="detail-grid">
                            <div className="detail-item">
                              <label>Pays:</label>
                              <span>{selectedMember.birthPlace.country}</span>
                            </div>
                            <div className="detail-item">
                              <label>Ville:</label>
                              <span>{selectedMember.birthPlace.city}</span>
                            </div>
                            {selectedMember.birthPlace.canton && (
                              <div className="detail-item">
                                <label>Canton:</label>
                                <span>{selectedMember.birthPlace.canton}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {selectedMember.address && (
                        <div className="detail-section">
                          <h3>Adresse de résidence</h3>
                          <div className="detail-grid">
                            <div className="detail-item">
                              <label>Rue:</label>
                              <span>{selectedMember.address.street} {selectedMember.address.streetNumber}</span>
                            </div>
                            <div className="detail-item">
                              <label>Ville:</label>
                              <span>{selectedMember.address.city}</span>
                            </div>
                            <div className="detail-item">
                              <label>Code postal:</label>
                              <span>{selectedMember.address.postalCode}</span>
                            </div>
                            {selectedMember.address.canton && (
                              <div className="detail-item">
                                <label>Canton:</label>
                                <span>{selectedMember.address.canton}</span>
                              </div>
                            )}
                            <div className="detail-item">
                              <label>Pays:</label>
                              <span>{selectedMember.address.country}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {selectedMember.identity && (
                        <div className="detail-section">
                          <h3>Pièce d'identité</h3>
                          <div className="detail-grid">
                            <div className="detail-item">
                              <label>Type:</label>
                              <span>{selectedMember.identity.type}</span>
                            </div>
                            <div className="detail-item">
                              <label>Numéro:</label>
                              <span>{selectedMember.identity.number}</span>
                            </div>
                            <div className="detail-item">
                              <label>Échéance:</label>
                              <span>{formatDate(selectedMember.identity.expiryDate)}</span>
                            </div>
                            <div className="detail-item">
                              <label>Date d&apos;émission:</label>
                              <span>{formatDate(selectedMember.identity.issueDate)}</span>
                            </div>
                            <div className="detail-item">
                              <label>Canton/Département d&apos;émission:</label>
                              <span>{selectedMember.identity.issueCanton || '—'}</span>
                            </div>
                            <div className="detail-item">
                              <label>Commune/localité d&apos;émission:</label>
                              <span>{selectedMember.identity.issueCity || '—'}</span>
                            </div>
                          </div>
                          
                          {/* Identity Photos */}
                          {(selectedMember.identity.copyFront || selectedMember.identity.copyBack) && (
                            <div className="identity-photos">
                              {selectedMember.identity.copyFront && (
                                <div className="identity-photo-item">
                                  <label>Copie recto:</label>
                                  <div className="photo-container">
                                    <img 
                                      src={getImageUrl(selectedMember.identity.copyFront)} 
                                      alt="Pièce d'identité recto"
                                      onError={(e) => {
                                        console.error('Error loading image:', getImageUrl(selectedMember.identity.copyFront));
                                        e.target.style.display = 'none';
                                        const errorDiv = e.target.parentElement.querySelector('.photo-error');
                                        if (errorDiv) {
                                          errorDiv.style.display = 'block';
                                        }
                                      }}
                                      onLoad={() => {
                                        console.log('Image loaded successfully:', getImageUrl(selectedMember.identity.copyFront));
                                      }}
                                    />
                                    <div className="photo-error" style={{ display: 'none' }}>
                                      Image non disponible
                                      <br />
                                      <small>Chemin: {selectedMember.identity.copyFront}</small>
                                    </div>
                                    <a 
                                      href={getImageUrl(selectedMember.identity.copyFront)} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="photo-link"
                                    >
                                      Ouvrir en grand
                                    </a>
                                  </div>
                                </div>
                              )}
                              {selectedMember.identity.copyBack && (
                                <div className="identity-photo-item">
                                  <label>Copie verso:</label>
                                  <div className="photo-container">
                                    <img 
                                      src={getImageUrl(selectedMember.identity.copyBack)} 
                                      alt="Pièce d'identité verso"
                                      onError={(e) => {
                                        console.error('Error loading image:', getImageUrl(selectedMember.identity.copyBack));
                                        e.target.style.display = 'none';
                                        const errorDiv = e.target.parentElement.querySelector('.photo-error');
                                        if (errorDiv) {
                                          errorDiv.style.display = 'block';
                                        }
                                      }}
                                      onLoad={() => {
                                        console.log('Image loaded successfully:', getImageUrl(selectedMember.identity.copyBack));
                                      }}
                                    />
                                    <div className="photo-error" style={{ display: 'none' }}>
                                      Image non disponible
                                      <br />
                                      <small>Chemin: {selectedMember.identity.copyBack}</small>
                                    </div>
                                    <a 
                                      href={getImageUrl(selectedMember.identity.copyBack)} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="photo-link"
                                    >
                                      Ouvrir en grand
                                    </a>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {selectedMember.codiceFiscale && (
                        <div className="detail-section">
                          <h3>Codice Fiscale</h3>
                          <div className="detail-grid">
                            <div className="detail-item">
                              <label>Numéro:</label>
                              <span>{selectedMember.codiceFiscale}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {selectedMember.juventusFanCard && selectedMember.juventusFanCard.has && (
                        <div className="detail-section">
                          <h3>Juventus Card</h3>
                          <div className="detail-grid">
                            <div className="detail-item">
                              <label>Numéro:</label>
                              <span>{selectedMember.juventusFanCard.number}</span>
                            </div>
                            <div className="detail-item">
                              <label>Échéance:</label>
                              <span>{formatDate(selectedMember.juventusFanCard.expiryDate)}</span>
                            </div>
                            <div className="detail-item">
                              <label>PIN:</label>
                              <span>{selectedMember.juventusFanCard.webticketPIN}</span>
                            </div>
                            {selectedMember.juventusFanCard.copy && (
                              <div className="detail-item full-width">
                                <label>Copie:</label>
                                <div className="photo-container">
                                  <img 
                                    src={getImageUrl(selectedMember.juventusFanCard.copy)} 
                                    alt="Juventus Card"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'block';
                                    }}
                                  />
                                  <div className="photo-error" style={{ display: 'none' }}>
                                    Image non disponible
                                  </div>
                                  <a 
                                    href={getImageUrl(selectedMember.juventusFanCard.copy)} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="photo-link"
                                  >
                                    Ouvrir en grand
                                  </a>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {selectedMember.subscription && selectedMember.subscription.has && (
                        <div className="detail-section">
                          <h3>Abonnement</h3>
                          <div className="detail-grid">
                            <div className="detail-item">
                              <label>Tribune:</label>
                              <span>{selectedMember.subscription.tribune}</span>
                            </div>
                            <div className="detail-item">
                              <label>Secteur:</label>
                              <span>{selectedMember.subscription.settore}</span>
                            </div>
                            <div className="detail-item">
                              <label>Rangée:</label>
                              <span>{selectedMember.subscription.fila}</span>
                            </div>
                            <div className="detail-item">
                              <label>Place:</label>
                              <span>{selectedMember.subscription.posto}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {selectedMember.blackWhiteCard && selectedMember.blackWhiteCard.has && (
                        <div className="detail-section">
                          <h3>Carte Black & White</h3>
                          <div className="detail-grid">
                            <div className="detail-item">
                              <label>Numéro:</label>
                              <span>{selectedMember.blackWhiteCard.number}</span>
                            </div>
                            <div className="detail-item">
                              <label>Échéance:</label>
                              <span>{formatDate(selectedMember.blackWhiteCard.expiryDate)}</span>
                            </div>
                            {selectedMember.blackWhiteCard.copy && (
                              <div className="detail-item full-width">
                                <label>Copie:</label>
                                <div className="photo-container">
                                  <img 
                                    src={getImageUrl(selectedMember.blackWhiteCard.copy)} 
                                    alt="Carte Black & White"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'block';
                                    }}
                                  />
                                  <div className="photo-error" style={{ display: 'none' }}>
                                    Image non disponible
                                  </div>
                                  <a 
                                    href={getImageUrl(selectedMember.blackWhiteCard.copy)} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="photo-link"
                                  >
                                    Ouvrir en grand
                                  </a>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {selectedMember.isMinor && selectedMember.parentInfo && (
                        <div className="detail-section">
                          <h3>Informations Parent (Mineur)</h3>
                          <div className="detail-grid">
                            <div className="detail-item">
                              <label>Nom:</label>
                              <span>{selectedMember.parentInfo.lastName}</span>
                            </div>
                            <div className="detail-item">
                              <label>Prénom:</label>
                              <span>{selectedMember.parentInfo.firstName}</span>
                            </div>
                            <div className="detail-item">
                              <label>Email:</label>
                              <span>{selectedMember.parentInfo.email}</span>
                            </div>
                            <div className="detail-item">
                              <label>Sexe:</label>
                              <span>{selectedMember.parentInfo.sexe}</span>
                            </div>
                            <div className="detail-item">
                              <label>Date de naissance:</label>
                              <span>
                                {selectedMember.parentInfo.dateOfBirth
                                  ? formatBirthDateDisplayUtc(
                                      selectedMember.parentInfo.dateOfBirth,
                                      'fr-CH',
                                    ) || '—'
                                  : '—'}
                              </span>
                            </div>
                            <div className="detail-item">
                              <label>Ville de naissance:</label>
                              <span>{selectedMember.parentInfo.birthCity}</span>
                            </div>
                            {selectedMember.parentInfo.birthCanton && (
                              <div className="detail-item">
                                <label>Canton:</label>
                                <span>{selectedMember.parentInfo.birthCanton}</span>
                              </div>
                            )}
                            {selectedMember.parentInfo.identityFile && (
                              <div className="detail-item full-width">
                                <label>Pièce d'identité parent:</label>
                                <div className="photo-container">
                                  <img 
                                    src={getImageUrl(selectedMember.parentInfo.identityFile)} 
                                    alt="Pièce d'identité parent"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'block';
                                    }}
                                  />
                                  <div className="photo-error" style={{ display: 'none' }}>
                                    Image non disponible
                                  </div>
                                  <a 
                                    href={getImageUrl(selectedMember.parentInfo.identityFile)} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="photo-link"
                                  >
                                    Ouvrir en grand
                                  </a>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {selectedMember.consents && (
                        <div className="detail-section">
                          <h3>Consentements</h3>
                          <div className="detail-grid">
                            <div className="detail-item">
                              <label>Marketing:</label>
                              <span>{selectedMember.consents.marketing ? '✓ Accepté' : '✗ Refusé'}</span>
                            </div>
                            <div className="detail-item">
                              <label>Promotion:</label>
                              <span>{selectedMember.consents.profiling ? '✓ Accepté' : '✗ Refusé'}</span>
                            </div>
                            <div className="detail-item">
                              <label>Droits d'image:</label>
                              <span>{selectedMember.consents.imageRights ? '✓ Accepté' : '✗ Refusé'}</span>
                            </div>
                            <div className="detail-item">
                              <label>Autorisation parentale:</label>
                              <span>{selectedMember.consents.minorAuthorization ? '✓ Accepté' : '✗ Refusé'}</span>
                            </div>
                            <div className="detail-item">
                              <label>Conditions générales:</label>
                              <span>{selectedMember.consents.termsAccepted ? '✓ Accepté' : '✗ Refusé'}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {selectedMember.payment && (
                        <div className="detail-section">
                          <h3>Paiement</h3>
                          <div className="detail-grid">
                            <div className="detail-item">
                              <label>Méthode:</label>
                              <span>{selectedMember.payment.method}</span>
                            </div>
                            <div className="detail-item">
                              <label>Type:</label>
                              <span>{selectedMember.payment.type}</span>
                            </div>
                            <div className="detail-item">
                              <label>Montant:</label>
                              <span>{selectedMember.payment.amount} {selectedMember.payment.currency}</span>
                            </div>
                            <div className="detail-item">
                              <label>Statut:</label>
                              <span className={`payment-status payment-${selectedMember.payment.status}`}>
                                {selectedMember.payment.status === 'pending' ? 'En attente' : 
                                 selectedMember.payment.status === 'completed' ? 'Complété' : 'Échoué'}
                              </span>
                            </div>
                            <div className="detail-item">
                              <label>Date:</label>
                              <span>{formatDate(selectedMember.payment.date)}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {selectedMember.otherFanClub && (
                        <div className="detail-section">
                          <h3>Autre Fan Club</h3>
                          <div className="detail-grid">
                            <div className="detail-item">
                              <label>Membre d'un autre fan club:</label>
                              <span>{selectedMember.otherFanClub.isMember ? 'Oui' : 'Non'}</span>
                            </div>
                            {selectedMember.otherFanClub.hasUnsubscribed && (
                              <div className="detail-item">
                                <label>Désinscription demandée:</label>
                                <span>✓</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                  >
                    <FaChevronLeft /> Précédent
                  </button>
                  
                  <div className="pagination-info">
                    Page <strong>{currentPage}</strong> sur <strong>{totalPages}</strong>
                  </div>
                  
                  <button
                    className="pagination-btn"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                  >
                    Suivant <FaChevronRight />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminNewMembers;
